import { Fragment, useState, useEffect } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { XMarkIcon, PhotoIcon, DocumentTextIcon, BuildingStorefrontIcon } from '@heroicons/react/24/outline'
import { colorMap } from '../../../Common'

const ModalContasRecebimento = ({ states, setters, actionCreate, actionEdit, color = 'orange' }) => {
    const colorClasses = colorMap[color] || colorMap.orange

    const [qrCode, setQrCode] = useState(null)
    const [qrCodePreview, setQrCodePreview] = useState(null)
    const [qrCodeAtual, setQrCodeAtual] = useState(null)
    const [nomeEmpresa, setNomeEmpresa] = useState('')
    const [nomeTitular, setNomeTitular] = useState('')
    const [cnpj, setCnpj] = useState('')
    const [email, setEmail] = useState('')
    const [telefone, setTelefone] = useState('')
    const [chavePix, setChavePix] = useState('')
    const [banco, setBanco] = useState('')
    const [agencia, setAgencia] = useState('')
    const [conta, setConta] = useState('')
    const [mostrarDadosBancarios, setMostrarDadosBancarios] = useState(false)

    const [errors, setErrors] = useState({})

    // Funções de formatação
    const formatCNPJ = (value) => {
        const cnpjClean = value.replace(/\D/g, '')
        if (cnpjClean.length <= 14) {
            return cnpjClean
                .replace(/^(\d{2})(\d)/, '$1.$2')
                .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
                .replace(/\.(\d{3})(\d)/, '.$1/$2')
                .replace(/(\d{4})(\d)/, '$1-$2')
        }
        return value
    }

    const formatPhone = (value) => {
        const phoneClean = value.replace(/\D/g, '')
        if (phoneClean.length <= 11) {
            return phoneClean
                .replace(/^(\d{2})(\d)/, '($1) $2')
                .replace(/(\d{5})(\d)/, '$1-$2')
        }
        return value
    }

    // Funções de validação
    const isValidCNPJ = (cnpj) => {
        const cnpjClean = cnpj.replace(/\D/g, '')
        return cnpjClean.length === 14
    }

    const isValidEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        return emailRegex.test(email)
    }

    const isValidPhone = (phone) => {
        const phoneClean = phone.replace(/\D/g, '')
        return phoneClean.length >= 10
    }

    const validate = () => {
        const newErrors = {}

        if (!nomeEmpresa.trim()) newErrors.nomeEmpresa = 'Nome da empresa é obrigatório'
        if (!nomeTitular.trim()) newErrors.nomeTitular = 'Nome do titular é obrigatório'

        if (!cnpj.trim()) {
            newErrors.cnpj = 'CNPJ é obrigatório'
        } else if (!isValidCNPJ(cnpj)) {
            newErrors.cnpj = 'CNPJ inválido'
        }

        if (!email.trim()) {
            newErrors.email = 'E-mail é obrigatório'
        } else if (!isValidEmail(email)) {
            newErrors.email = 'E-mail inválido'
        }

        if (!telefone.trim()) {
            newErrors.telefone = 'Telefone é obrigatório'
        } else if (!isValidPhone(telefone)) {
            newErrors.telefone = 'Telefone inválido'
        }

        if (!chavePix.trim()) newErrors.chavePix = 'Chave PIX é obrigatória'
        if (!qrCode && !states.isEdit) newErrors.qrCode = 'QR Code é obrigatório'

        const hasBanco = banco.trim() !== ''
        const hasAgencia = agencia.trim() !== ''
        const hasConta = conta.trim() !== ''

        if (hasBanco || hasAgencia || hasConta) {
            if (!hasBanco) newErrors.banco = 'Banco é obrigatório quando dados bancários são informados'
            if (!hasAgencia) newErrors.agencia = 'Agência é obrigatória quando dados bancários são informados'
            if (!hasConta) newErrors.conta = 'Conta é obrigatória quando dados bancários são informados'
        }

        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const handleQrCodeChange = (e) => {
        const file = e.target.files[0]
        if (file) {
            setQrCode(file)
            const reader = new FileReader()
            reader.onloadend = () => {
                setQrCodePreview(reader.result)
            }
            reader.readAsDataURL(file)
            setErrors({ ...errors, qrCode: null })
        }
    }

    const removeQrCode = () => {
        setQrCode(null)
        setQrCodePreview(null)
    }

    const handleSubmit = async () => {
        if (!validate()) {
            return
        }

        if (states.isEdit) {
            if (!qrCode) {
                const jsonData = {
                    nome_empresa: nomeEmpresa,
                    nome_titular: nomeTitular,
                    cnpj: cnpj,
                    email: email,
                    telefone: telefone,
                    chave_pix: chavePix
                }

                if (banco.trim() !== '') {
                    jsonData.banco = banco
                    jsonData.agencia = agencia
                    jsonData.conta = conta
                } else {
                    jsonData.banco = null
                    jsonData.agencia = null
                    jsonData.conta = null
                }

                actionEdit(states.ContasRecebimento.id, jsonData)
                return
            } else {
                const formData = new FormData()
                formData.append('nome_empresa', nomeEmpresa)
                formData.append('nome_titular', nomeTitular)
                formData.append('cnpj', cnpj)
                formData.append('email', email)
                formData.append('telefone', telefone)
                formData.append('chave_pix', chavePix)

                if (banco.trim() !== '') {
                    formData.append('banco', banco)
                    formData.append('agencia', agencia)
                    formData.append('conta', conta)
                }

                formData.append('qrcode', qrCode)
                actionEdit(states.ContasRecebimento.id, formData)
                return
            }
        }

        const formData = new FormData()
        formData.append('nome_empresa', nomeEmpresa)
        formData.append('nome_titular', nomeTitular)
        formData.append('cnpj', cnpj)
        formData.append('email', email)
        formData.append('telefone', telefone)
        formData.append('chave_pix', chavePix)

        if (banco.trim() !== '') {
            formData.append('banco', banco)
            formData.append('agencia', agencia)
            formData.append('conta', conta)
        }

        if (qrCode) {
            formData.append('qrcode', qrCode)
        }

        actionCreate(formData)
    }

    useEffect(() => {
        if (!states.modalContasRecebimento) {
            setQrCode(null)
            setQrCodePreview(null)
            setQrCodeAtual(null)
            setNomeEmpresa('')
            setNomeTitular('')
            setCnpj('')
            setEmail('')
            setTelefone('')
            setChavePix('')
            setBanco('')
            setAgencia('')
            setConta('')
            setMostrarDadosBancarios(false)
            setErrors({})
        } else {
            if (states.isEdit && states.ContasRecebimento) {
                const conta = states.ContasRecebimento
                setNomeEmpresa(conta.nome_empresa || '')
                setNomeTitular(conta.nome_titular || '')
                setCnpj(conta.cnpj || '')
                setEmail(conta.email || '')
                setTelefone(conta.telefone || '')
                setChavePix(conta.chave_pix || '')
                setBanco(conta.banco || '')
                setAgencia(conta.agencia || '')
                setConta(conta.conta || '')

                if (conta.banco || conta.agencia || conta.conta) {
                    setMostrarDadosBancarios(true)
                }

                if (conta.qrcode_path &&
                    conta.qrcode_path !== 'null' &&
                    conta.qrcode_path.trim() !== '' &&
                    conta.qrcode_path.length > 50) {

                    if (conta.qrcode_path.startsWith('data:')) {
                        setQrCodeAtual(conta.qrcode_path)
                    } else if (conta.qrcode_path.startsWith('base64_')) {
                        const base64Data = conta.qrcode_path.replace('base64_', '')
                        const imageType = conta.qrcode_nome_arquivo?.includes('.png') ? 'png' :
                            conta.qrcode_nome_arquivo?.includes('.jpg') || conta.qrcode_nome_arquivo?.includes('.jpeg') ? 'jpeg' : 'webp'
                        setQrCodeAtual(`data:image/${imageType};base64,${base64Data}`)
                    } else {
                        const imageType = conta.qrcode_nome_arquivo?.includes('.png') ? 'png' :
                            conta.qrcode_nome_arquivo?.includes('.jpg') || conta.qrcode_nome_arquivo?.includes('.jpeg') ? 'jpeg' : 'webp'
                        setQrCodeAtual(`data:image/${imageType};base64,${conta.qrcode_path}`)
                    }
                } else if (conta.qrcode_nome_arquivo &&
                    conta.qrcode_path !== 'null') {
                    setQrCodeAtual(`/api/images/${conta.qrcode_nome_arquivo}`)
                }
            } else {
                setQrCodeAtual(null)
            }
        }
    }, [states.modalContasRecebimento, states.isEdit, states.ContasRecebimento])

    return (
        <Transition.Root show={states.modalContasRecebimento} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={() => setters.setModalContasRecebimento(false)}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                </Transition.Child>

                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <Dialog.Panel className="relative transform overflow-hidden rounded-2xl bg-white px-4 pb-6 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl sm:p-6">
                                <div className="absolute right-0 top-0 pr-4 pt-4">
                                    <button
                                        type="button"
                                        className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transition-colors"
                                        onClick={() => setters.setModalContasRecebimento(false)}
                                    >
                                        <span className="sr-only">Fechar</span>
                                        <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                                    </button>
                                </div>

                                <div className={`border-b ${states.isEdit ? colorClasses.border : 'border-gray-200'} pb-4`}>
                                    <div className={`inline-flex items-center justify-center w-full px-4 py-2 rounded-lg ${states.isEdit ? colorClasses.containerBg : 'bg-gray-50'} mb-3`}>
                                        <span className={`text-sm font-semibold ${states.isEdit ? colorClasses.textGradient : 'text-gray-700'}`}>
                                            {states.isEdit ? '✏️ Modo Edição' : '➕ Nova Conta'}
                                        </span>
                                    </div>
                                    <h3 className={`text-2xl font-bold leading-7 text-center ${states.isEdit ? colorClasses.textGradient : 'text-gray-900'}`}>
                                        {states.isEdit ? 'Editar Conta de Recebimento' : 'Nova Conta de Recebimento'}
                                    </h3>
                                    <p className="mt-2 text-sm text-gray-500 text-center">
                                        {states.isEdit ? 'Atualize as informações da conta abaixo' : 'Preencha os dados para criar uma nova conta'}
                                    </p>
                                </div>

                                <div className="mt-6 space-y-8">
                                    <div className="border-b border-gray-200 pb-8">
                                        <div className="flex items-center gap-3 mb-4">
                                            <PhotoIcon className="h-5 w-5 text-orange-600" />
                                            <h4 className="text-base font-semibold text-gray-900">QR Code PIX</h4>
                                        </div>
                                        <div className="mt-4 flex justify-center">
                                            {qrCodePreview ? (
                                                <div className="relative w-full max-w-xs">
                                                    <img
                                                        src={qrCodePreview}
                                                        alt="Preview do QR Code"
                                                        className="h-32 w-32 sm:h-40 sm:w-40 mx-auto object-contain rounded-lg border-2 border-gray-200 bg-white"
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={removeQrCode}
                                                        className="absolute top-1 right-1 sm:top-2 sm:right-2 rounded-full bg-red-500 p-1 sm:p-1.5 text-white hover:bg-red-600 transition-colors"
                                                    >
                                                        <XMarkIcon className="h-3 w-3 sm:h-4 sm:w-4" />
                                                    </button>
                                                    <div className="absolute bottom-1 left-1 sm:bottom-2 sm:left-2 bg-green-500 text-white text-xs px-1.5 py-0.5 sm:px-2 sm:py-1 rounded">
                                                        Novo QR Code
                                                    </div>
                                                </div>
                                            ) : qrCodeAtual ? (
                                                <div className="relative w-full max-w-xs">
                                                    <img
                                                        src={qrCodeAtual}
                                                        alt="QR Code atual"
                                                        className="h-32 w-32 sm:h-40 sm:w-40 mx-auto object-contain rounded-lg border-2 border-gray-200 bg-white"
                                                    />
                                                    <label className="absolute inset-0 flex flex-col items-center justify-center bg-black bg-opacity-0 hover:bg-opacity-50 transition-all cursor-pointer rounded-lg group">
                                                        <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                                                            <PhotoIcon className="w-6 h-6 sm:w-8 sm:h-8 mb-1 text-white mx-auto" />
                                                            <p className="text-xs sm:text-sm text-white font-semibold px-2 text-center">Trocar QR Code</p>
                                                        </div>
                                                        <input
                                                            type="file"
                                                            className="hidden"
                                                            accept="image/*"
                                                            onChange={handleQrCodeChange}
                                                        />
                                                    </label>
                                                    {states.isEdit && (
                                                        <div className="absolute top-1 left-1 sm:top-2 sm:left-2 bg-brand-cyan text-white text-xs px-1.5 py-0.5 sm:px-2 sm:py-1 rounded">
                                                            QR Code atual
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                <label className="flex flex-col items-center justify-center w-full max-w-xs h-32 sm:h-40 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors">
                                                    <div className="flex flex-col items-center justify-center">
                                                        <PhotoIcon className="w-8 h-8 sm:w-10 sm:h-10 mb-2 text-gray-400" />
                                                        <p className="mb-1 text-xs sm:text-sm text-gray-500 text-center px-2">
                                                            <span className="font-semibold">Clique para upload</span>
                                                        </p>
                                                        <p className="text-xs text-gray-500">PNG, JPG, WEBP</p>
                                                    </div>
                                                    <input
                                                        type="file"
                                                        className="hidden"
                                                        accept="image/*"
                                                        onChange={handleQrCodeChange}
                                                    />
                                                </label>
                                            )}
                                        </div>
                                        {errors.qrCode && (
                                            <p className="mt-2 text-sm text-red-600 text-center">{errors.qrCode}</p>
                                        )}
                                    </div>

                                    <div className="border-b border-gray-200 pb-8">
                                        <div className="flex items-center gap-3 mb-4">
                                            <DocumentTextIcon className="h-5 w-5 text-orange-600" />
                                            <h4 className="text-base font-semibold text-gray-900">Informações Obrigatórias</h4>
                                        </div>
                                        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-6">
                                            <div className="sm:col-span-2">
                                                <label htmlFor="nomeEmpresa" className="block text-sm font-medium leading-6 text-gray-900">
                                                    Nome da Empresa <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    id="nomeEmpresa"
                                                    name="nomeEmpresa"
                                                    value={nomeEmpresa}
                                                    onChange={(e) => {
                                                        setNomeEmpresa(e.target.value)
                                                        if (errors.nomeEmpresa) setErrors({ ...errors, nomeEmpresa: null })
                                                    }}
                                                    className={`mt-2 block w-full rounded-lg border-0 py-2.5 px-4 text-gray-900 shadow-sm ring-1 ring-inset ${errors.nomeEmpresa ? 'ring-red-300 focus:ring-red-500' : 'ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-orange-600'} placeholder:text-gray-400 sm:text-sm sm:leading-6 transition-all`}
                                                    placeholder="Digite o nome da empresa"
                                                />
                                                {errors.nomeEmpresa && (
                                                    <p className="mt-1 text-sm text-red-600">{errors.nomeEmpresa}</p>
                                                )}
                                            </div>

                                            <div>
                                                <label htmlFor="nomeTitular" className="block text-sm font-medium leading-6 text-gray-900">
                                                    Nome do Titular da Conta <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    id="nomeTitular"
                                                    name="nomeTitular"
                                                    value={nomeTitular}
                                                    onChange={(e) => {
                                                        setNomeTitular(e.target.value)
                                                        if (errors.nomeTitular) setErrors({ ...errors, nomeTitular: null })
                                                    }}
                                                    className={`mt-2 block w-full rounded-lg border-0 py-2.5 px-4 text-gray-900 shadow-sm ring-1 ring-inset ${errors.nomeTitular ? 'ring-red-300 focus:ring-red-500' : 'ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-orange-600'} placeholder:text-gray-400 sm:text-sm sm:leading-6 transition-all`}
                                                    placeholder="Digite o nome do titular"
                                                />
                                                {errors.nomeTitular && (
                                                    <p className="mt-1 text-sm text-red-600">{errors.nomeTitular}</p>
                                                )}
                                            </div>

                                            <div>
                                                <label htmlFor="cnpj" className="block text-sm font-medium leading-6 text-gray-900">
                                                    CNPJ <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    id="cnpj"
                                                    name="cnpj"
                                                    value={cnpj}
                                                    onChange={(e) => {
                                                        setCnpj(formatCNPJ(e.target.value))
                                                        if (errors.cnpj) setErrors({ ...errors, cnpj: null })
                                                    }}
                                                    className={`mt-2 block w-full rounded-lg border-0 py-2.5 px-4 text-gray-900 shadow-sm ring-1 ring-inset ${errors.cnpj ? 'ring-red-300 focus:ring-red-500' : 'ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-orange-600'} placeholder:text-gray-400 sm:text-sm sm:leading-6 transition-all`}
                                                    placeholder="00.000.000/0000-00"
                                                    maxLength={18}
                                                />
                                                {errors.cnpj && (
                                                    <p className="mt-1 text-sm text-red-600">{errors.cnpj}</p>
                                                )}
                                            </div>

                                            <div>
                                                <label htmlFor="email" className="block text-sm font-medium leading-6 text-gray-900">
                                                    E-mail <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    type="email"
                                                    id="email"
                                                    name="email"
                                                    value={email}
                                                    onChange={(e) => {
                                                        setEmail(e.target.value)
                                                        if (errors.email) setErrors({ ...errors, email: null })
                                                    }}
                                                    className={`mt-2 block w-full rounded-lg border-0 py-2.5 px-4 text-gray-900 shadow-sm ring-1 ring-inset ${errors.email ? 'ring-red-300 focus:ring-red-500' : 'ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-orange-600'} placeholder:text-gray-400 sm:text-sm sm:leading-6 transition-all`}
                                                    placeholder="exemplo@email.com"
                                                />
                                                {errors.email && (
                                                    <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                                                )}
                                            </div>

                                            <div>
                                                <label htmlFor="telefone" className="block text-sm font-medium leading-6 text-gray-900">
                                                    Telefone <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    id="telefone"
                                                    name="telefone"
                                                    value={telefone}
                                                    onChange={(e) => {
                                                        setTelefone(formatPhone(e.target.value))
                                                        if (errors.telefone) setErrors({ ...errors, telefone: null })
                                                    }}
                                                    className={`mt-2 block w-full rounded-lg border-0 py-2.5 px-4 text-gray-900 shadow-sm ring-1 ring-inset ${errors.telefone ? 'ring-red-300 focus:ring-red-500' : 'ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-orange-600'} placeholder:text-gray-400 sm:text-sm sm:leading-6 transition-all`}
                                                    placeholder="(00) 00000-0000"
                                                    maxLength={15}
                                                />
                                                {errors.telefone && (
                                                    <p className="mt-1 text-sm text-red-600">{errors.telefone}</p>
                                                )}
                                            </div>

                                            <div className="sm:col-span-2">
                                                <label htmlFor="chavePix" className="block text-sm font-medium leading-6 text-gray-900">
                                                    Chave PIX <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    id="chavePix"
                                                    name="chavePix"
                                                    value={chavePix}
                                                    onChange={(e) => {
                                                        setChavePix(e.target.value)
                                                        if (errors.chavePix) setErrors({ ...errors, chavePix: null })
                                                    }}
                                                    className={`mt-2 block w-full rounded-lg border-0 py-2.5 px-4 text-gray-900 shadow-sm ring-1 ring-inset ${errors.chavePix ? 'ring-red-300 focus:ring-red-500' : 'ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-orange-600'} placeholder:text-gray-400 sm:text-sm sm:leading-6 transition-all`}
                                                    placeholder="Digite a chave PIX"
                                                />
                                                {errors.chavePix && (
                                                    <p className="mt-1 text-sm text-red-600">{errors.chavePix}</p>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    <div>
                                        <div className="flex items-center justify-between mb-4">
                                            <div className="flex items-center gap-3">
                                                <BuildingStorefrontIcon className="h-5 w-5 text-orange-600" />
                                                <h4 className="text-base font-semibold text-gray-900">Dados Bancários (Opcional)</h4>
                                            </div>
                                            {!mostrarDadosBancarios && (
                                                <button
                                                    type="button"
                                                    onClick={() => setMostrarDadosBancarios(true)}
                                                    className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${colorClasses.headerButtonBg} text-white ${colorClasses.headerButtonHover}`}
                                                >
                                                    + Adicionar Dados Bancários
                                                </button>
                                            )}
                                        </div>

                                        {mostrarDadosBancarios && (
                                            <>
                                                <div className="flex items-center justify-between mb-4">
                                                    <p className="text-xs text-gray-500">
                                                        Se preencher algum campo bancário, todos devem ser preenchidos
                                                    </p>
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setMostrarDadosBancarios(false)
                                                            setBanco('')
                                                            setAgencia('')
                                                            setConta('')
                                                            setErrors({ ...errors, banco: null, agencia: null, conta: null })
                                                        }}
                                                        className="text-sm text-red-600 hover:text-red-700 font-medium"
                                                    >
                                                        Remover
                                                    </button>
                                                </div>
                                                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-6">
                                                    <div>
                                                        <label htmlFor="banco" className="block text-sm font-medium leading-6 text-gray-900">
                                                            Banco
                                                        </label>
                                                        <input
                                                            type="text"
                                                            id="banco"
                                                            name="banco"
                                                            value={banco}
                                                            onChange={(e) => {
                                                                setBanco(e.target.value)
                                                                if (errors.banco) setErrors({ ...errors, banco: null })
                                                            }}
                                                            className={`mt-2 block w-full rounded-lg border-0 py-2.5 px-4 text-gray-900 shadow-sm ring-1 ring-inset ${errors.banco ? 'ring-red-300 focus:ring-red-500' : 'ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-orange-600'} placeholder:text-gray-400 sm:text-sm sm:leading-6 transition-all`}
                                                            placeholder="Ex: Banco do Brasil"
                                                        />
                                                        {errors.banco && (
                                                            <p className="mt-1 text-sm text-red-600">{errors.banco}</p>
                                                        )}
                                                    </div>

                                                    <div>
                                                        <label htmlFor="agencia" className="block text-sm font-medium leading-6 text-gray-900">
                                                            Agência
                                                        </label>
                                                        <input
                                                            type="text"
                                                            id="agencia"
                                                            name="agencia"
                                                            value={agencia}
                                                            onChange={(e) => {
                                                                setAgencia(e.target.value)
                                                                if (errors.agencia) setErrors({ ...errors, agencia: null })
                                                            }}
                                                            className={`mt-2 block w-full rounded-lg border-0 py-2.5 px-4 text-gray-900 shadow-sm ring-1 ring-inset ${errors.agencia ? 'ring-red-300 focus:ring-red-500' : 'ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-orange-600'} placeholder:text-gray-400 sm:text-sm sm:leading-6 transition-all`}
                                                            placeholder="Ex: 1234-5"
                                                        />
                                                        {errors.agencia && (
                                                            <p className="mt-1 text-sm text-red-600">{errors.agencia}</p>
                                                        )}
                                                    </div>

                                                    <div className="sm:col-span-2">
                                                        <label htmlFor="conta" className="block text-sm font-medium leading-6 text-gray-900">
                                                            Conta
                                                        </label>
                                                        <input
                                                            type="text"
                                                            id="conta"
                                                            name="conta"
                                                            value={conta}
                                                            onChange={(e) => {
                                                                setConta(e.target.value)
                                                                if (errors.conta) setErrors({ ...errors, conta: null })
                                                            }}
                                                            className={`mt-2 block w-full rounded-lg border-0 py-2.5 px-4 text-gray-900 shadow-sm ring-1 ring-inset ${errors.conta ? 'ring-red-300 focus:ring-red-500' : 'ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-orange-600'} placeholder:text-gray-400 sm:text-sm sm:leading-6 transition-all`}
                                                            placeholder="Ex: 12345-6"
                                                        />
                                                        {errors.conta && (
                                                            <p className="mt-1 text-sm text-red-600">{errors.conta}</p>
                                                        )}
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>

                                <div className="mt-8 flex items-center justify-end gap-x-4 border-t border-gray-200 pt-6">
                                    <button
                                        type="button"
                                        onClick={() => setters.setModalContasRecebimento(false)}
                                        className="rounded-lg bg-white px-4 py-2.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 transition-colors"
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleSubmit}
                                        className={`rounded-lg px-4 py-2.5 text-sm font-semibold text-white shadow-sm ${colorClasses.headerButtonBg} ${colorClasses.headerButtonHover} focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ${colorClasses.focusRingOffset} transition-all`}
                                    >
                                        {states.isEdit ? 'Salvar Alterações' : 'Criar Conta'}
                                    </button>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    )
}

export default ModalContasRecebimento
